<i18n>
  {
    "fr": {
      "placeholder": "Sélectionner le/les voyageur(s)",
      "travellers": "1 Voyageur | {n} Voyageurs",
      "years": "Moins d'un an | 1 an | {n} ans",
      "errors": {
        "required": "Ce champ est requis",
        "adt-required": "Au moins un adulte est requis à la date de départ"
      }
    },
    "en": {
      "placeholder": "Select traveller(s)",
      "travellers": "1 Traveller | {n} Travellers",
      "years": "Less than a year old | 1 year old | {n} years old",
      "errors": {
        "required": "Required field",
        "adt-required": "At least one adult is required at departure date"
      }
    }
  }
</i18n>

<template>
  <v-select
    id="travellers-select"
    v-model="selectedTravellers"
    data-cy="travellers-select"
    solo
    flat
    :label="$i18n.t('placeholder')"
    multiple
    :items="travellers"
    item-text="formatName"
    return-object
    color="primary"
    background-color="primaryLight"
    :error-messages="errors"
    @input="selectedTravellersTouch"
    @blur="selectedTravellersTouch"
  >
    <template #prepend-inner>
      <v-icon
        color="primary"
        class="mr-2"
      >
        mdi-account
      </v-icon>
    </template>
    <template #selection="{index}">
      <div
        v-if="index === 0"
        data-cy="travellers-select"
      >
        <span>
          {{ $i18n.tc('travellers', selectedTravellers.length) }}
        </span>
      </div>
    </template>
    <template #item="{item, attrs}">
      <v-checkbox
        v-model="attrs.inputValue"
      />
      <v-list-item-content
        :data-cy="`travellers-select-list-item-${item.registration_number}`"
        v-text="item.formatName"
      />
    </template>
  </v-select>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import { mapMutations, mapState } from 'vuex'
import { validationMixin } from 'vuelidate'
import { required } from 'vuelidate/lib/validators'
import { adultValidator } from '@/validators/adultValidator'

export default {
  name: 'FlightSelectTravellers',
  mixins: [validationMixin],
  computed: {
    ...mapState({
      travellers: state => state.fb.passengersInformation.travellers,
      searchParametersTravellers: state => state.fb.passengersInformation.searchParametersTravellers,
      outwardDate: state => state.fb.searchParameters.outwardDate,
      returnDate: state => state.fb.searchParameters.returnDate
    }),
    selectedTravellers: {
      /**
       * Just return vueX store state
       * @return {string}
       */
      get () {
        return this.searchParametersTravellers
      },
      /**
       * Call to a vueX store commit method
       * @param newValue {string}
       */
      set (newValue) {
        if (this.outwardDate) {
          this.setTravellerType(newValue)
        }
        this.changeSearchParametersTravellers(newValue)
      }
    },
    /**
     * This value need to be computed for the watcher to work properly
     * @return {boolean}
     */
    isValid () {
      // This value need to be computed for the watcher to work properly
      return !this.$v.$invalid
    },
    /**
     * Return an array of string with errors or nothing based on vuelidate state
     * @return {string[]}
     */
    errors () {
      const errors = []
      if (!this.$v.selectedTravellers.$dirty) return errors
      !this.$v.selectedTravellers.required && errors.push(this.$i18n.t('errors.required'))
      !this.$v.selectedTravellers.adultValidator && errors.push(this.$i18n.t('errors.adt-required'))
      return errors
    }
  },
  watch: {
    /**
     * Watch for value change of validity then commit the new value to the store
     * (based on isDeparture prop)
     * @param newVal {boolean}
     */
    isValid (newVal) {
      // TODO: icicicic verifier que l'age des enfants se met bien a jour
      this.changeTravellersValidity(newVal)
    },
    /**
     * Every time the date change, travellers may need a new 'traveller type' (ADT, CNN or INF)
     * which is required by the supplier.
     * This watcher take care of assign a new type to each selected travellers
     */
    returnDate () {
      const travellers = _.cloneDeep(this.selectedTravellers)
      this.setTravellerType(travellers)
      this.selectedTravellers = travellers
    },
    outwardDate () {
      const travellers = _.cloneDeep(this.selectedTravellers)
      this.setTravellerType(travellers)
      this.selectedTravellers = travellers
    }
  },
  validations: {
    selectedTravellers: {
      required,
      adultValidator
    }
  },
  methods: {
    ...mapMutations({
      changeSearchParametersTravellers: 'fb/passengersInformation/changeSearchParametersTravellers',
      changeTravellersValidity: 'fb/searchParameters/changeTravellersValidity'
    }),
    /**
     * Shortcut for vuelidate touch method
     */
    selectedTravellersTouch () {
      this.$v.selectedTravellers.$touch()
    },
    /**
     * This method add a new attribute to each traveller object to give him a type.
     * (ADT | CNN | INF)
     * @param travellers {Object[]}
     */
    setTravellerType (travellers) {
      for (const traveller of travellers) {
        const ageAt = this.returnDate ? this.ageAtReturn(traveller) : this.ageAtDeparture(traveller)

        if (ageAt >= 12) {
          traveller.traveller_type = 'ADT'
        } else if (ageAt >= 2 && ageAt < 12) {
          traveller.traveller_type = 'CNN'
        } else {
          traveller.traveller_type = 'INF'
        }
      }
    },
    /**
     * Return the age of the traveller at departure date (needed to know his type)
     * @param traveller {Object}
     * @return {number} Age in years
     */
    ageAtDeparture (traveller) {
      const departureDate = moment(this.outwardDate)
      const birthday = moment(traveller.birthday)
      return departureDate.diff(birthday, 'years')
    },
    ageAtReturn (traveller) {
      const returnDate = moment(this.returnDate)
      const birthday = moment(traveller.birthday)
      return returnDate.diff(birthday, 'years')
    }
  }
}
</script>

<style scoped>

</style>
